import {baseAPIGenerationUrl} from "../constants";


export async function fetchGallery(tags = [], offset = 0, isVideo = null, token = null, isPublic = true, sortPopular = true, sortNewest = false, limit = 20) {
    const params = new URLSearchParams();
    tags.forEach(tag => params.append('tags', tag));
    params.append('offset', offset.toString());
    params.append('limit', limit.toString());
    if (isVideo !== null) {
        params.append('is_video', isVideo.toString());
    }

    params.append('sort_popular', sortPopular.toString());
    params.append('sort_newest', sortNewest.toString());

    const endpoint = isPublic ? 'gallery_public' : 'gallery';
    const options = {};
    if (token) {
        options.headers = { 'Authorization': `Bearer ${token}` };
    }

    const response = await fetch(`${baseAPIGenerationUrl}/${endpoint}?${params}`, options);
    return response.json();
}

/**
 * Generate an image using the backend API
 * @param {string} page - The page making the request
 * @param {string} token - Authorization token
 * @param {File} img - The image file to upload
 * @param {Object} cfg - Configuration for image generation
 * @returns {Promise<Object>} - Response with status and task_id
 */
export async function generateImage(token, img, cfg) {
  try {
    // Create form data
    const formData = new FormData();
    formData.append('image', img);
    formData.append('cfg', JSON.stringify(cfg));

    // Set up request options
    const options = {
      method: 'POST',
      body: formData,
      headers: {}
    };

    // Add authorization header if token is provided
    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    }

    // Make the request
    const response = await fetch(`${baseAPIGenerationUrl}/generate`, options);


    // Parse and return the response
    return [await response.json(), response.status];
  } catch (error) {
    console.error('Error generating image:', error);
    throw error;
  }
}


export async function checkGeneration(token) {
  try {
    const response = await fetch(`${baseAPIGenerationUrl}/check_task`, {
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });


    // Parse and return the response
    return [await response.json(), response.status];
  } catch (error) {
    console.error('Error generating image:', error);
    throw error;
  }
}


export async function generateImage2(token, cfg) {
  try {
    // Create form data
    const formData = new FormData();
    formData.append('cfg', JSON.stringify(cfg));

    // Set up request options
    const options = {
      method: 'POST',
      body: formData,
      headers: {}
    };

    // Add authorization header if token is provided
    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    }

    // Make the request
    const response = await fetch(`${baseAPIGenerationUrl}/generate`, options);


    // Parse and return the response
    return [await response.json(), response.status];
  } catch (error) {
    console.error('Error generating image:', error);
    throw error;
  }
}



export async function chatRouter(token, messages) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ messages }),
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error chat_router:', error);
        throw error;
    }
}


export async function fetchOnboardingMessages(token, userPrompt, lang) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/onboarding_messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ user_prompt: userPrompt, lang }),
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error onboarding_messages:', error);
        throw error;
    }
}

// Fast first call — returns just intro + nudge + paywall (depends on user_prompt).
export async function fetchOnboardingIntro(token, userPrompt, lang) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/onboarding_intro`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ user_prompt: userPrompt, lang }),
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error onboarding_intro:', error);
        throw error;
    }
}

// Background call — returns after-state narrations only (depends only on lang).
export async function fetchOnboardingRest(token, lang) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/onboarding_rest`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ lang }),
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error onboarding_rest:', error);
        throw error;
    }
}


export async function chatTitle(token, messages) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/chat_title`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ messages }),
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error chat_title:', error);
        throw error;
    }
}


export async function editSuggestions(token, imagePrompt, language) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/edit_suggestions`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ image_prompt: imagePrompt, language: language || '' }),
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error edit_suggestions:', error);
        throw error;
    }
}


export async function fetchStatus(token, taskId) {
    if (token) {
        const response = await fetch(`${baseAPIGenerationUrl}/status?task_id=${taskId}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        console.log('[fetchStatus] HTTP status:', response.status, 'for task:', taskId);

        // Check for auth errors before parsing JSON
        if (response.status === 401) {
            console.error('[fetchStatus] Auth error (401) - token may be invalid');
            return { httpStatus: 401, authError: true };
        }

        if (!response.ok) {
            console.error('[fetchStatus] HTTP error:', response.status);
            return { httpStatus: response.status, error: true };
        }

        const data = await response.json();
        return { ...data, httpStatus: response.status };
    } else {
        // throw new Error("Need token");
    }
}


export async function fetchTags(isVideo) {
    const response = await fetch(`${baseAPIGenerationUrl}/tags?is_video=${isVideo}`);
    return response.json();
}


export async function fetchPrompt(token, cfg) {
    const formData = new FormData();
    formData.append('cfg', JSON.stringify(cfg));
    const response = await fetch(`${baseAPIGenerationUrl}/prompt`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
    });
    return response.json();
}


export async function fetchRemixData(token, mediaId) {
    const response = await fetch(`${baseAPIGenerationUrl}/remix_data?media_id=${mediaId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    return response.json();
}


export async function animate(token, taskId, action, batchIdx, sampleIdx = null, mediaId = null, promptText = null) {
    try {
        const body = { action };
        if (taskId) body.task_id = taskId;
        if (batchIdx !== undefined && batchIdx !== null) body.batch_idx = batchIdx;
        if (sampleIdx !== null) body.sample_idx = sampleIdx;
        if (mediaId !== null) body.media_id = mediaId;
        if (promptText) body.prompt_text = promptText;

        const options = {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json'
            }
        };

        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${baseAPIGenerationUrl}/animate`, options);
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error animating image:', error);
        throw error;
    }
}


export async function editAdmin(token, imageFile, promptText) {
    try {
        const formData = new FormData();
        formData.append('image', imageFile);
        formData.append('prompt_text', promptText);

        const options = {
            method: 'POST',
            body: formData,
            headers: {}
        };

        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${baseAPIGenerationUrl}/edit_admin`, options);
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error editing image (admin):', error);
        throw error;
    }
}


export async function edit(token, taskId, prompt, batchIdx, sampleIdx = null, mediaId = null) {
    try {
        const body = { prompt };
        if (taskId) body.task_id = taskId;
        if (batchIdx !== undefined && batchIdx !== null) body.batch_idx = batchIdx;
        if (sampleIdx !== null) body.sample_idx = sampleIdx;
        if (mediaId !== null) body.media_id = mediaId;

        const options = {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json'
            }
        };

        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${baseAPIGenerationUrl}/edit`, options);
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error editing image:', error);
        throw error;
    }
}


export async function likeMedia(token, mediaId) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/like?media_id=${mediaId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error liking media:', error);
        throw error;
    }
}


export async function deleteMedia(token, mediaId) {
    try {
        const response = await fetch(`${baseAPIGenerationUrl}/media?media_id=${mediaId}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        if (response.status === 204 || response.status === 200) {
            return [null, response.status];
        }
        return [await response.json(), response.status];
    } catch (error) {
        console.error('Error deleting media:', error);
        throw error;
    }
}


/**
 * Fetch user photos for video generation
 * @param {string} token - Authorization token
 * @param {string} videoAction - The video action type (cowgirl, missionary, etc.)
 * @returns {Promise<Array>} - Array of user photos [{url: "...", media_id: "..."}, ...]
 */
export async function fetchRandomPrompt(token) {
    const response = await fetch(`${baseAPIGenerationUrl}/random_prompt`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return response.json();
}


export async function fetchUserPhotos(token, videoAction) {
    let response = null;
    if (videoAction) {
        response = await fetch(`${baseAPIGenerationUrl}/source_images?action=${videoAction}`, {
            headers: {'Authorization': `Bearer ${token}`}
        });
    } else {
        response = await fetch(`${baseAPIGenerationUrl}/source_images`, {
            headers: {'Authorization': `Bearer ${token}`}
        });
    }
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    return response.json();
}


export async function createCall(token, agentId) {
    const response = await fetch(`${baseAPIGenerationUrl}/call?agent_id=${agentId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return [await response.json(), response.status];
}

export async function deleteCall(token) {
    const response = await fetch(`${baseAPIGenerationUrl}/call`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return [await response.json(), response.status];
}

export async function callHeartbeat(token, callSessionId) {
    const response = await fetch(`${baseAPIGenerationUrl}/call_heartbeat?call_session_id=${callSessionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return [await response.json(), response.status];
}

export async function getCallAgent(token, agentId) {
    const response = await fetch(`${baseAPIGenerationUrl}/call/agent?agent_id=${agentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
    });
    return [await response.json(), response.status];
}

export async function getCallAgents(token) {
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const response = await fetch(`${baseAPIGenerationUrl}/call/agents`, { headers });
    return [await response.json(), response.status];
}

export async function getCallAgentsTop(token) {
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const response = await fetch(`${baseAPIGenerationUrl}/call/agents_top`, { headers });
    return [await response.json(), response.status];
}

export async function createCallAgent(token, config) {
    const response = await fetch(`${baseAPIGenerationUrl}/call/agent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
    });
    return [await response.json(), response.status];
}

export async function getChatSuggestions(token, messages) {
    const response = await fetch(`${baseAPIGenerationUrl}/chat/suggestions`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages })
    });
    return [await response.json(), response.status];
}

export async function submitTakeItDownReport({ name, email, description, url, media_id }) {
    const payload = { name, email, description };
    if (media_id) payload.media_id = media_id;
    if (url) payload.url = url;
    const response = await fetch(`${baseAPIGenerationUrl}/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });
    let data = null;
    try { data = await response.json(); } catch (e) { /* ignore */ }
    return [data, response.status];
}